import { all, dropTask, task } from 'ember-concurrency';
import { fn } from '@ember/helper';
import { Footer, Modal } from 'tio-ui/components/modal';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import { Input } from 'tio-ui/components/forms';
import TioClickableText from '../../tio/clickable-text.gts';
import type Store from '@ember-data/store';
import type TasFieldOptionModel from '../../../models/tas-field-option.ts';
import type TasProgramTemplateModel from '../../../models/tas-program-template.ts';
import type Owner from '@ember/owner';
import { Button } from 'tio-ui/components/buttons';
import { on } from '@ember/modifier';
import { PhPlus, PhX } from 'ember-phosphor-icons';

interface TuitionAssistanceFieldOptionsEditDialogSignature {
  Args: {
    programTemplate: TasProgramTemplateModel;
    fieldName: string;
    fieldLabel: string;
    fieldOptions: TasFieldOptionModel[];
    didCancel: () => void;
    didSubmit: () => void;
  };
}

export default class TuitionAssistanceFieldOptionsEditDialogComponent extends Component<TuitionAssistanceFieldOptionsEditDialogSignature> {
  @service declare store: Store;
  @tracked fieldOptions: TasFieldOptionModel[] = [];

  constructor(owner: Owner, args: TuitionAssistanceFieldOptionsEditDialogSignature['Args']) {
    super(owner, args);
    this.fieldOptions = args.fieldOptions.slice();
  }

  // In general this component needs better error handling
  saveFieldOptions = dropTask(async () => {
    const childTasks: Promise<TasFieldOptionModel>[] = [];
    this.fieldOptions.forEach((option) => {
      childTasks.push(this.saveFieldOption.perform(option));
    });
    await all(childTasks);
    this.args.didSubmit();
  });

  saveFieldOption = task({ maxConcurrency: 10, enqueue: true }, async (model) => {
    // NOTE: For now we are setting the label to the value. Update this if/when we expose a label field.
    model.label = model.value;
    await model.save();
    return model;
  });

  deleteFieldOption = (fieldOption: TasFieldOptionModel) => {
    // TODO: We don't have a great way of handling ordinal resets here. Ordinal management is a bit of a mess right now.
    fieldOption.deleteRecord();
  };

  addFieldOption = () => {
    const newFieldOption = this.store.createRecord('tas-field-option', {
      tasProgramTemplate: this.args.programTemplate,
      fieldName: this.args.fieldName,
      ordinal: this.fieldOptions.length,
      value: '',
      label: '',
    });
    // @ts-expect-error: Array spread with tracked array type issue
    this.fieldOptions = [...this.fieldOptions, newFieldOption];
  };

  updateFieldOptionAttr = (fieldOption: TasFieldOptionModel, attr: string, value: string) => {
    // @ts-expect-error: Dynamic property access on model
    fieldOption[attr] = value.trim();
  };

  onSubmit = () => {
    this.saveFieldOptions.perform();
  };

  onCancel = () => {
    this.fieldOptions.forEach((option) => {
      option.rollbackAttributes();
    });
    this.args.didCancel();
  };

  <template>
    <Modal @isOpen={{true}} @onClose={{this.onCancel}} class="min-h-64" @backdrop="faded" as |m|>
      <m.Header>{{@fieldLabel}}</m.Header>
      <m.Body>
        <form>
          <table>
            <thead>
              <tr>
                <th class="w-16">{{t "common.order"}}</th>
                <th class="">{{t "common.value"}}</th>
                <th class=""></th>
              </tr>
            </thead>
            <tbody>
              {{#each this.fieldOptions as |fieldOption|}}
                {{#unless fieldOption.isDeleted}}
                  <tr>
                    <td class="">
                      <Input
                        @size="sm"
                        aria-label="Ordinal"
                        name="ordinal"
                        @value="{{fieldOption.ordinal}}"
                        @onInput={{fn this.updateFieldOptionAttr fieldOption "ordinal"}}
                        @type="number"
                      />
                    </td>
                    <td class="">
                      <Input
                        @size="sm"
                        aria-label="Value"
                        name="value"
                        @value={{fieldOption.value}}
                        @onInput={{fn this.updateFieldOptionAttr fieldOption "value"}}
                      />
                    </td>
                    <td class="">
                      <Button
                        @appearance="outlined"
                        {{on "click" (fn this.deleteFieldOption fieldOption)}}
                      >
                        <PhX @size="20" />
                      </Button>
                    </td>
                  </tr>
                {{/unless}}
              {{/each}}
            </tbody>
          </table>

          <TioClickableText
            @onClick={{this.addFieldOption}}
            @textClass="tio-anchor"
            @icon={{PhPlus}}
          >
            {{t "common.tuition_assistance.program_template_review.add_options"}}
          </TioClickableText>
        </form>
      </m.Body>
      <m.Footer>
        <Footer
          @isRunning={{this.saveFieldOptions.isRunning}}
          @onCancel={{this.onCancel}}
          @onSubmit={{this.onSubmit}}
        />
      </m.Footer>
    </Modal>
  </template>
}
